import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCommentDots } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import DemandeAutres from './DemandeAutres';
import PiecesJointes from './PiecesJointes';
import { formaterJour } from './formatAvances';

/**
 * Destination « Autres demandes » de l'espace collaborateur (Yros/Kavero#433) :
 * le formulaire de dépôt, puis la liste de ses demandes de ce type.
 *
 * Le socle rend toutes les demandes du salarié dans la même enveloppe : le
 * filtre par type n'est pas cosmétique, sans lui cette page afficherait des
 * congés sous un titre qui n'en parle pas (même doctrine que « Avances »).
 *
 * L'action retenue par la RH n'apparaît qu'une fois la demande décidée : c'est
 * un libellé que le salarié lit, rien n'est exécuté.
 */

const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

function MesAutresDemandes() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(false);
    try {
      const reponse = await apiService.listerDemandesRh();
      setDemandes((reponse?.demandes || []).filter((demande) => demande.type === 'autres'));
    } catch (e) {
      signalerErreur(e, 'MesAutresDemandes.charger');
      setDemandes([]);
      setErreur(true);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  // La plus récente d'abord : c'est celle qui intéresse le salarié juste après
  // un dépôt.
  const triees = useMemo(
    () => [...demandes].sort((a, b) => (b.date_demande || '').localeCompare(a.date_demande || '')),
    [demandes]
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <h1 className="text-lg sm:text-xl font-bold text-slate-800">{t('mesAutres.titre')}</h1>

      <DemandeAutres onDepot={charger} />

      <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100">
        <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
          <span className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center flex-shrink-0">
            <FaRegCommentDots className="w-4 h-4" aria-hidden="true" />
          </span>
          <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
            {t('mesAutres.listeTitre')}
          </h3>
        </div>

        {erreur && (
          <p
            data-testid="mes-autres-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {t('mesAutres.erreurGenerique')}
          </p>
        )}

        {chargement ? (
          <LoadingBarWithStyles />
        ) : !erreur && triees.length === 0 ? (
          <p data-testid="mes-autres-vide" className="py-8 text-center text-sm text-gray-500">
            {t('mesAutres.vide')}
          </p>
        ) : (
          !erreur && (
            <ul className="divide-y divide-slate-100">
              {triees.map((demande) => {
                const contenu = demande.contenu || {};
                return (
                  <li
                    key={demande.id}
                    data-testid={`mes-autres-ligne-${demande.id}`}
                    className="py-3 flex flex-col gap-1.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-slate-900 whitespace-pre-line break-words min-w-0">
                        {contenu.objet}
                      </p>
                      <span
                        className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${
                          COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
                        }`}
                      >
                        {t(`mesDemandes.etats.${demande.etat}`, { defaultValue: demande.etat })}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {contenu.motif
                        ? `${t(`demandePret.motifs.${contenu.motif}`, { defaultValue: contenu.motif })} · `
                        : ''}
                      {t('mesAutres.demandeeLe', { date: formaterJour(demande.date_demande, locale) })}
                    </p>
                    {/* Ce que la RH a retenu : un libellé à lire, jamais un effet. */}
                    {contenu.action && (
                      <p data-testid={`mes-autres-action-${demande.id}`} className="text-xs text-slate-700">
                        <span className="font-medium">{t('mesAutres.actionRetenue')} : </span>
                        {contenu.action.nom}
                      </p>
                    )}
                    {demande.motif_refus && (
                      <p data-testid={`mes-autres-motif-${demande.id}`} className="text-xs text-red-800">
                        <span className="font-medium">{t('mesDemandes.motifRefus')} : </span>
                        {demande.motif_refus}
                      </p>
                    )}
                    <PiecesJointes demande={demande} />
                  </li>
                );
              })}
            </ul>
          )
        )}
      </div>
    </div>
  );
}

export default MesAutresDemandes;
